/**
 * SF-5057: the "Delete now" bar above the In progress table while deletions are ticked. Asks
 * for confirmation naming every company, then forces each one (deleted now, not after the
 * waiting period) and reports each company's outcome.
 */
import { useRef, useState } from 'react';
import { useDataProvider, useInvalidate } from '@refinedev/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Modal, Space, Typography } from 'antd';

import { companyDeletionsQueryKey, type CompanyDeletionRow } from '../api/companyDeleteClient';
import { forceDeletionForCompanies, type ForceBatchResult } from '../deletionForce';

const companies = (n: number) => `${n} ${n === 1 ? 'company' : 'companies'}`;
const nameOf = (row: CompanyDeletionRow) => row.company_name || 'Unnamed company';

interface Target {
  id: string;
  name: string;
}

interface Report extends ForceBatchResult {
  names: Map<string, string>;
}

interface ForceDeleteBarProps {
  /** Ticked deletions, as last loaded (the page re-checks them on each refresh). */
  selected: CompanyDeletionRow[];
  /** False while the person's company list is unknown: nothing can be started. */
  enabled: boolean;
  /** Companies now being deleted, so the table can untick them. */
  onDone: (companyIds: string[]) => void;
  onClear: () => void;
}

function ReportList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <>
      <Typography.Text strong>{title}</Typography.Text>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </>
  );
}

export const ForceDeleteBar: React.FC<ForceDeleteBarProps> = ({
  selected,
  enabled,
  onDone,
  onClear,
}) => {
  const dataProvider = useDataProvider();
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  // Set at once on confirm, before the mutation state re-renders, so a second confirm can't
  // start an overlapping run.
  const busy = useRef(false);

  // The runner never rejects (each company's failure is in its result), so there is no onError.
  const mutation = useMutation({
    mutationFn: (targets: Target[]) =>
      forceDeletionForCompanies(
        dataProvider(),
        targets.map((t) => t.id),
      ),
    onSuccess: (outcome, targets) => {
      setReport({ ...outcome, names: new Map(targets.map((t) => [t.id, t.name])) });
      if (outcome.result.updated.length > 0) onDone(outcome.result.updated);
      // Refresh after refusals too: a refusal means the list was out of date.
      void queryClient.invalidateQueries({ queryKey: companyDeletionsQueryKey });
      void invalidate({ resource: 'companies', invalidates: ['resourceAll'] });
    },
    onSettled: () => {
      busy.current = false;
    },
  });
  const running = mutation.isLoading;
  const count = selected.length;

  // Stays mounted while a batch runs or its report shows, even if every tick is cleared.
  if (count === 0 && !running && !report) return null;

  const confirm = () => {
    setConfirming(false);
    if (busy.current || !enabled || selected.length === 0) return;
    busy.current = true;
    setReport(null);
    // Copied now: the ticks can change while it runs, and the report keeps the names.
    mutation.mutate(selected.map((r) => ({ id: r.company_id, name: nameOf(r) })));
  };

  const name = (id: string) => report?.names.get(id) ?? id;
  const refused = report?.result.failed.filter((f) => report.refused.has(f.id)) ?? [];
  const failed = report?.result.failed.filter((f) => !report.refused.has(f.id)) ?? [];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space wrap align="center" aria-label="Force delete">
        <Typography.Text strong>{companies(count)} selected</Typography.Text>
        <Button
          danger
          disabled={!enabled || running || count === 0}
          loading={running}
          onClick={() => setConfirming(true)}
        >
          Delete now ({count})
        </Button>
        <Button type="link" onClick={onClear} disabled={running}>
          Clear selection
        </Button>
      </Space>
      {report && (
        <Alert
          type={report.result.failed.length === 0 ? 'success' : 'warning'}
          showIcon
          closable
          onClose={() => setReport(null)}
          message={`Delete now: ${companies(report.result.updated.length)} deleting, ${companies(
            refused.length,
          )} refused, ${companies(failed.length)} failed`}
          description={
            <Space direction="vertical" size={4}>
              <ReportList title="Deleting now" items={report.result.updated.map(name)} />
              <ReportList
                title="Refused"
                items={refused.map((f) => `${name(f.id)}: ${f.message}`)}
              />
              <ReportList title="Failed" items={failed.map((f) => `${name(f.id)}: ${f.message}`)} />
            </Space>
          }
        />
      )}
      <Modal
        open={confirming}
        title={`Delete ${companies(count)} now?`}
        okText="Confirm"
        okButtonProps={{ danger: true, disabled: !enabled || count === 0 }}
        onOk={confirm}
        onCancel={() => setConfirming(false)}
      >
        <Typography.Paragraph>
          You are deleting {companies(count)} now. This can&apos;t be undone.
        </Typography.Paragraph>
        <ul style={{ maxHeight: 240, overflowY: 'auto', paddingLeft: 20 }}>
          {selected.map((r) => (
            <li key={r.company_id}>{nameOf(r)}</li>
          ))}
        </ul>
      </Modal>
    </Space>
  );
};
